import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ApiError } from '../../services/api';
import { LoadingSpinner } from './LoadingSpinner';

const ErrorContainer = styled.div<{ $variant: 'compact' | 'full' }>`
  display: flex;
  flex-direction: ${({ $variant }) => $variant === 'compact' ? 'row' : 'column'};
  align-items: center;
  gap: ${({ theme, $variant }) => $variant === 'compact' ? theme.spacing.md : theme.spacing.lg};
  padding: ${({ theme, $variant }) => $variant === 'compact' ? theme.spacing.md : theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.error}05;
  border: 1px solid ${({ theme }) => theme.colors.error}30;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-align: ${({ $variant }) => $variant === 'compact' ? 'left' : 'center'};
  width: 100%;
  transition: all ${({ theme }) => theme.transitions.gallery.hover};
  
  /* Mobile-first responsive design */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing.mobile.md};
    padding: ${({ theme }) => theme.spacing.mobile.padding};
    background-color: ${({ theme }) => theme.colors.error}08;
    border: 2px solid ${({ theme }) => theme.colors.error}25;
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    text-align: center;
    box-shadow: ${({ theme }) => theme.shadows.gallery.soft};
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: ${({ $variant }) => $variant === 'compact' ? 'column' : 'column'};
    padding: ${({ theme, $variant }) => $variant === 'compact' ? theme.spacing.lg : theme.spacing.xl};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    gap: ${({ theme }) => theme.spacing.lg};
  }
  
  /* Enhanced desktop styling */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
    box-shadow: ${({ theme }) => theme.shadows.gallery.subtle};
    
    &:hover {
      box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
    }
  }
`;

const ErrorIcon = styled.div<{ $variant: 'compact' | 'full' }>`
  font-size: ${({ $variant }) => $variant === 'compact' ? '24px' : '48px'};
  color: ${({ theme }) => theme.colors.error};
  flex-shrink: 0;
`;

const ErrorContent = styled.div<{ $variant: 'compact' | 'full' }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: ${({ $variant }) => $variant === 'compact' ? 'flex-start' : 'center'};
`;

const ErrorTitle = styled.h3<{ $variant: 'compact' | 'full' }>`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme, $variant }) => 
    $variant === 'compact' ? theme.typography.fontSize.base : theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const ErrorMessage = styled.p<{ $variant: 'compact' | 'full' }>`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme, $variant }) => 
    $variant === 'compact' ? theme.typography.fontSize.sm : theme.typography.fontSize.base};
  line-height: 1.5;
`;

const RetrySection = styled.div<{ $variant: 'compact' | 'full' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme, $variant }) => $variant === 'compact' ? '0' : theme.spacing.md};
  flex-shrink: 0;
`;

const RetryButton = styled.button<{ $size: 'sm' | 'md' }>`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme, $size }) => 
    $size === 'sm' 
      ? `${theme.spacing.sm} ${theme.spacing.md}` 
      : `${theme.spacing.md} ${theme.spacing.lg}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme, $size }) => 
    $size === 'sm' ? theme.typography.fontSize.sm : theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.gallery.hover};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  min-height: 44px; /* Accessibility minimum */
  box-shadow: ${({ theme }) => theme.shadows.gallery.subtle};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.gallery.soft};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
  
  /* Mobile-first responsive design */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.mobile.md} ${({ theme }) => theme.spacing.mobile.lg};
    font-size: ${({ theme }) => theme.typography.fontSize.mobile.body};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.semibold};
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    min-height: ${({ theme }) => theme.breakpoints.touchTarget};
    width: 100%;
    box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
    
    &:active:not(:disabled) {
      transform: scale(0.98);
      transition: transform ${({ theme }) => theme.transitions.gallery.instant};
    }
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme, $size }) => 
      $size === 'sm' 
        ? `${theme.spacing.md} ${theme.spacing.lg}` 
        : `${theme.spacing.lg} ${theme.spacing.xl}`};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    min-width: 120px;
  }
  
  /* Enhanced desktop styling */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
    
    &:hover:not(:disabled) {
      box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
    }
  }
`;

const RetryInfo = styled.div<{ $size: 'sm' | 'md' }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme, $size }) => 
    $size === 'sm' ? theme.typography.fontSize.xs : theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.medium};
`;

const RetryCounter = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const NextRetryTime = styled.div`
  opacity: 0.8;
`;

const ManualActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  justify-content: center;
  
  /* Mobile-first responsive layout */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.mobile.sm};
    margin-top: ${({ theme }) => theme.spacing.mobile.md};
    width: 100%;
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: ${({ theme }) => theme.spacing.md};
    margin-top: ${({ theme }) => theme.spacing.md};
    justify-content: center;
  }
  
  /* Enhanced desktop layout */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    gap: ${({ theme }) => theme.spacing.gallery.tiny};
    margin-top: ${({ theme }) => theme.spacing.gallery.sm};
  }
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.gray.medium};
  color: ${({ theme }) => theme.colors.gray.dark};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.gallery.hover};
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray.light};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
  
  /* Mobile-first responsive design */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.mobile.sm} ${({ theme }) => theme.spacing.mobile.md};
    font-size: ${({ theme }) => theme.typography.fontSize.mobile.body};
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    min-height: ${({ theme }) => theme.breakpoints.touchTarget};
    border-width: 2px;
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.medium};
    
    &:active {
      transform: scale(0.98);
      background-color: ${({ theme }) => theme.colors.mobile.background};
    }
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    min-height: 40px;
  }
  
  /* Enhanced desktop styling */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.subtle};
    
    &:hover {
      box-shadow: ${({ theme }) => theme.shadows.gallery.subtle};
    }
  }
`;

export interface RetryConfig {
  maxAttempts?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  enableAutoRetry?: boolean;
}

export interface RetryableErrorProps {
  error: ApiError;
  onRetry: () => Promise<void> | void;
  variant?: 'compact' | 'full';
  retryConfig?: RetryConfig;
  showManualActions?: boolean;
  onDismiss?: () => void;
  onReportIssue?: () => void;
  className?: string;
}

export function RetryableError({
  error,
  onRetry,
  variant = 'full',
  retryConfig = {},
  showManualActions = true,
  onDismiss,
  onReportIssue,
  className,
}: RetryableErrorProps) {
  const {
    maxAttempts = 3,
    initialDelay = 1000,
    maxDelay = 30000,
    backoffFactor = 2,
    enableAutoRetry = true,
  } = retryConfig;

  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [nextRetryIn, setNextRetryIn] = useState<number | null>(null);
  const [autoRetryEnabled, setAutoRetryEnabled] = useState(enableAutoRetry && error.isRetryable);

  const calculateDelay = useCallback((attempt: number) => {
    const delay = Math.min(initialDelay * Math.pow(backoffFactor, attempt), maxDelay);
    return delay;
  }, [initialDelay, maxDelay, backoffFactor]);

  const handleRetry = useCallback(async (isAutoRetry = false) => {
    if (isRetrying || retryCount >= maxAttempts) return;

    setIsRetrying(true);
    
    try {
      await onRetry();
      // If successful, reset retry count
      setRetryCount(0);
      setNextRetryIn(null);
    } catch (retryError) {
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);
      
      // Schedule next auto retry if enabled and within limits
      if (autoRetryEnabled && newRetryCount < maxAttempts && !isAutoRetry) {
        const delay = calculateDelay(newRetryCount);
        setNextRetryIn(delay);
        
        const timer = setTimeout(() => {
          handleRetry(true);
        }, delay);
        
        // Countdown timer
        const countdown = setInterval(() => {
          setNextRetryIn(prev => {
            if (prev === null || prev <= 1000) {
              clearInterval(countdown);
              return null;
            }
            return prev - 1000;
          });
        }, 1000);
        
        return () => {
          clearTimeout(timer);
          clearInterval(countdown);
        };
      }
    } finally {
      setIsRetrying(false);
    }
  }, [isRetrying, retryCount, maxAttempts, onRetry, autoRetryEnabled, calculateDelay]);

  // Auto-retry effect
  useEffect(() => {
    if (autoRetryEnabled && error.isRetryable && retryCount === 0) {
      const timer = setTimeout(() => {
        handleRetry(true);
      }, calculateDelay(0));
      
      return () => clearTimeout(timer);
    }
  }, [autoRetryEnabled, error.isRetryable, retryCount, handleRetry, calculateDelay]);

  const canRetry = error.isRetryable && retryCount < maxAttempts;
  const hasExceededRetries = retryCount >= maxAttempts;

  const getErrorTitle = () => {
    if (hasExceededRetries) {
      return 'Service Unavailable';
    }
    
    switch (error.type) {
      case 'service_outage':
        return 'Service Temporarily Down';
      case 'network':
        return 'Connection Issue';
      case 'server_error':
        return 'Server Error';
      default:
        return 'Error Occurred';
    }
  };

  const getErrorMessage = () => {
    if (hasExceededRetries) {
      return 'We were unable to complete your request after multiple attempts. Please try again later or contact support if the problem persists.';
    }
    
    return error.message;
  };

  const formatTimeRemaining = (ms: number) => {
    const seconds = Math.ceil(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.ceil(seconds / 60);
    return `${minutes}m`;
  };

  return (
    <ErrorContainer $variant={variant} className={className}>
      <ErrorIcon $variant={variant}>
        {error.type === 'service_outage' && '⚠️'}
        {error.type === 'network' && '📡'}
        {error.type === 'server_error' && '🔧'}
        {!['service_outage', 'network', 'server_error'].includes(error.type) && '❌'}
      </ErrorIcon>
      
      <ErrorContent $variant={variant}>
        <ErrorTitle $variant={variant}>{getErrorTitle()}</ErrorTitle>
        <ErrorMessage $variant={variant}>{getErrorMessage()}</ErrorMessage>
        
        {canRetry && (
          <RetrySection $variant={variant}>
            <RetryButton
              $size={variant === 'compact' ? 'sm' : 'md'}
              onClick={() => handleRetry(false)}
              disabled={isRetrying || nextRetryIn !== null}
            >
              {isRetrying && <LoadingSpinner size="sm" color="white" />}
              {isRetrying ? 'Retrying...' : 'Try Again'}
            </RetryButton>
            
            {retryCount > 0 && (
              <RetryInfo $size={variant === 'compact' ? 'sm' : 'md'}>
                <RetryCounter>
                  Attempt {retryCount}/{maxAttempts}
                </RetryCounter>
                {nextRetryIn && (
                  <NextRetryTime>
                    Auto-retry in {formatTimeRemaining(nextRetryIn)}
                  </NextRetryTime>
                )}
              </RetryInfo>
            )}
          </RetrySection>
        )}
        
        {autoRetryEnabled && canRetry && (
          <ActionButton onClick={() => setAutoRetryEnabled(false)}>
            Stop Auto-Retry
          </ActionButton>
        )}
        
        {showManualActions && (variant === 'full' || hasExceededRetries) && (
          <ManualActions>
            {onDismiss && (
              <ActionButton onClick={onDismiss}>
                Dismiss
              </ActionButton>
            )}
            {onReportIssue && (
              <ActionButton onClick={onReportIssue}>
                Report Issue
              </ActionButton>
            )}
            <ActionButton onClick={() => window.location.reload()}>
              Refresh Page
            </ActionButton>
          </ManualActions>
        )}
      </ErrorContent>
    </ErrorContainer>
  );
}

// Hook for managing retryable errors
export function useRetryableError(retryConfig?: RetryConfig) {
  const [error, setError] = useState<ApiError | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const showError = useCallback((newError: ApiError) => {
    setError(newError);
    setRetryCount(0);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setRetryCount(0);
  }, []);

  const incrementRetryCount = useCallback(() => {
    setRetryCount(prev => prev + 1);
  }, []);

  return {
    error,
    retryCount,
    showError,
    clearError,
    incrementRetryCount,
    ErrorComponent: error ? (
      <RetryableError
        error={error}
        onRetry={clearError}
        retryConfig={retryConfig}
        onDismiss={clearError}
      />
    ) : null,
  };
}